<template>
    <div>
        <table v-if="bookings && bookings.length > 0">
            <thead>
                <tr>
                    <th class="your-time-column">Time</th>
                    <th class="door-code-column">Doorcode</th>
                    <th class="booking-number-column">Bookingnumber</th>
                    <th class="client-column">Client</th>
                    <th class="team-column">Team</th>
                    <th class="players-column">Players</th>
                    <th class="email-column">Email</th>
                    <th class="language-column">Language</th>
                    <th class="minutes-played-column">Minutes Played</th>
                    <th class="experience-column">Experience</th>
                    <th class="score-column">Score</th>
                    <th class="game-state-column">Game state</th>
                    <th class="rating-column">Rating</th>
                    <th class="xcube-column">X-Cube</th>
                    <th class="booking-info-column">Booking Info</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="booking of bookings" :key="booking.id" class="content-tr">
                    <td class="your-time-column">
                        <div class="time-stack">
                            <span class="time-block-user">
                                <span
                                    v-if="!isInUserTimezone(booking.location_time_zone)"
                                    :class="flagClass(countryCodeBasedOnTimeZone())"
                                ></span>
                                {{ getTimeInCurrentTimeZone(booking.date) }}
                            </span>
                            <span
                                v-if="!isInUserTimezone(booking.location_time_zone)"
                                class="time-block-location"
                            >
                                <span :class="flagClass(booking.location_country)"></span>
                                {{
                                    getTimeInTimeZone(
                                        booking.date,
                                        booking.location_time_zone
                                    )
                                }}
                            </span>
                        </div>
                    </td>
                    <td class="door-code-column">
                        <span>{{ booking.door_code }}</span>
                    </td>
                    <td class="booking-number-column">
                        <span>{{ booking.code }}</span>
                    </td>
                    <td class="client-column">
                        <span>{{ formatBookingClientName(booking) }}</span>
                    </td>
                    <td class="team-column">
                        <span>{{ booking.team_name }}</span>
                    </td>
                    <td class="players-column">
                        <span>{{ booking.player_count }}</span>
                    </td>
                    <td class="email-column">
                        <span>{{ booking.email }}</span>
                    </td>
                    <td class="language-column">
                        <span>{{ booking.language }}</span>
                    </td>
                    <td class="minutes-played-column">
                        <span>{{ booking.minutes_played }}</span>
                    </td>
                    <td class="experience-column">
                        <span class="mobile-only">
                            {{ booking.team_name }}
                        </span>
                        <span>{{
                            booking.experience_name
                        }}</span>
                        <span class="mobile-only">
                            {{ booking.xcube_name }}
                        </span>
                    </td>
                    <td class="score-column">
                        <span>{{ booking.score }}</span>
                        <font-awesome-icon v-if="booking.finished" class="finish-flag-icon"
                            icon="fa-solid fa-flag-checkered" />
                    </td>
                    <td class="game-state-column">
                        <status-mark v-bind:checked="hasState(booking)"></status-mark>
                    </td>
                    <td class="rating-column">
                        <div v-if="booking.rating">
                            {{ booking.rating }}
                            <font-awesome-icon icon="fa-solid fa-gamepad" />
                        </div>
                        <div v-if="booking.rating_location">
                            {{ booking.rating_location }}
                            <font-awesome-icon icon="fa-solid fa-building" />
                        </div>
                    </td>
                    <td class="xcube-column">
                        <span>{{
                            booking.location_name
                        }}</span>
                        <span>
                            {{ booking.xcube_name }}
                        </span>
                        <span>
                            {{ booking.xcube_letter }}
                        </span>
                    </td>
                    <td class="booking-info-column">
                        <router-link :to="{ name: 'view-booking', params: { id: booking.id, booking } }">
                            <span class="view-button">View</span>
                        </router-link>
                    </td>
                </tr>
            </tbody>
        </table>
        <div v-else>No Bookings</div>
    </div>
</template>

<script setup>
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { defineProps } from "vue";
import { useRouter } from "vue-router/composables";
import { formatBookingClientName } from "../../helpers/bookingTableRow";
import {
    countryCodeBasedOnTimeZone,
    getTimeInCurrentTimeZone,
    getTimeInTimeZone,
    isInUserTimezone,
} from "../../helpers/dates";
import StatusMark from "./../shared/StatusMark.vue";

dayjs.extend(utc);
dayjs.extend(timezone);

const router = useRouter();

const props = defineProps({
    bookings: {
        type: Array,
        default: () => [],
    },
});

const flagClass = (country) => {
    if (!country) {
        return "";
    }
    return `fi fi-${country.toLowerCase()}`;
};

const hasState = (booking) => {
    return booking.minutes_played > 5 || booking.score > 0;
};

const showBooking = (booking) => {
    router.push({
        name: "view-booking",
        params: { id: booking.id, booking: booking },
    });
};
</script>

<style scoped lang="scss">
@use "../../colors" as *;

table {
    width: 100%;
    table-layout: auto;
    border-collapse: collapse;
    border-width: 0;

    th,
    td {
        padding: 0.75rem 0.6rem;
        height: auto;
        vertical-align: middle;
        overflow-wrap: break-word;
    }
}

.door-code-column,
.players-column,
.language-column,
.minutes-played-column,
.score-column,
.game-state-column,
.rating-column,
.booking-info-column {
    width: 1%;
    white-space: nowrap;
    overflow-wrap: normal;
}

.your-time-column {
    width: 1%;
    white-space: nowrap;
    overflow-wrap: normal;
}

.email-column {
    min-width: 12rem;
}

.booking-number-column,
.experience-column {
    width: 16%;
}

.view-button {
    cursor: pointer;
    border: 1px solid $color-primary;
    border-radius: 5px;
    padding: 3px 5px;
    color: white;
}

.content-tr:hover {
    background-color: rgba($color-background, 0.2);
}

.finish-flag-icon {
    padding-left: 0.5rem;
    color: $color-info;
}

.time-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
}

.time-block-user,
.time-block-location {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
}

.time-block-location {
    color: $color-primary;
}

tr:nth-child(odd) {
    background-color: rgba($color-background, 0.8);
}

@media (max-width: 1830px) {

    .xcube-time-column,
    .door-code-column {
        display: none;
    }
}

@media (max-width: 1750px) {
    .client-column {
        display: none;
    }
}

@media (max-width: 1600px) {
    .email-column {
        display: none;
    }
}

@media (max-width: 1280px) {

    .minutes-played-column,
    .language-column {
        display: none;
    }
}

@media (max-width: 1140px) {
    .team-column {
        display: none;
    }
}

@media (max-width: 1024px) {
    .booking-number-column {
        display: none;
    }
}

@media (max-width: 768px) {

    .score-column,
    .game-state-column,
    .rating-column {
        display: none;
    }
}

@media (min-width: 512px) {
    .mobile-only {
        display: none;
    }
}

@media (max-width: 512px) {

    .players-column,
    .xcube-column {
        display: none;
    }

    .mobile-only {
        display: block;
    }
}
</style>
